import React, { useState } from 'react';

import ModalWrapper from '../ModalWrapper';
import FormField from '../../components/common/FormField';
import { csrfFetch } from '../../store/csrf';
import './ContactFormModal.css';

// Validation helpers
const validatePhone = (phone) => {
  if (!phone) return true; // Optional field
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.length === 10;
};

const validateEmail = (email) => {
  if (!email) return true; // Optional field
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validateName = (name) => {
  const trimmed = (name || '').trim();
  if (trimmed.length < 1 || trimmed.length > 100) return false;
  // Allow real-world names: letters (incl. accented), spaces, apostrophes,
  // hyphens, and periods (e.g. "O'Brien", "Mary-Jane", "José", "J.R. Smith").
  // Must start with a letter; rejects markup and stray digits.
  return /^\p{L}[\p{L}\s'.-]*$/u.test(trimmed);
};

const validateMessage = (message) => {
  const trimmed = (message || '').trim();
  // Any non-empty message up to a sane length. Punctuation like & / - is fine;
  // React escapes on render, so we don't reject legitimate content here.
  return trimmed.length >= 1 && trimmed.length <= 5000;
};

// Helper function to format phone with +1
const formatPhone = (phone) => {
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.length === 10 ? `+1${digitsOnly}` : phone;
};

const ContactFormModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    name: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({
    phone: '',
    email: '',
    name: '',
    message: '',
    contact: '' // For the "at least one contact method" error
  });
  
  const [, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Validate individual fields
  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'phone':
        if (value && !validatePhone(value)) {
          error = 'Phone number must be exactly 10 digits';
        }
        break;
      case 'email':
        if (value && !validateEmail(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'name':
        if (!validateName(value)) {
          error = 'Name is required and must be valid';
        }
        break;
      case 'message':
        if (!validateMessage(value)) {
          error = 'Message is required';
        }
        break;
      default:
        break;
    }
    return error;
  };
  
  // Check if at least one contact method is provided
  const validateContactMethods = () => {
    const hasPhone = formData.phone.trim().length > 0;
    const hasEmail = formData.email.trim().length > 0;
    return hasPhone || hasEmail;
  };
  
  // Handle input changes
  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear any existing errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear contact method error if user starts typing in either field
    if ((name === 'phone' || name === 'email') && value.trim() && errors.contact) {
      setErrors(prev => ({ ...prev, contact: '' }));
    }
  };
  
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };
  
  // Check if form is valid
  const isFormValid = () => {
    const hasValidName = validateName(formData.name);
    const hasValidMessage = validateMessage(formData.message);
    const hasValidPhone = validatePhone(formData.phone);
    const hasValidEmail = validateEmail(formData.email);
    const hasContactMethod = validateContactMethods();
    const noErrors = Object.values(errors).every(err => err === '');
    
    return hasValidName && hasValidMessage && hasValidPhone && hasValidEmail && hasContactMethod && noErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields on submit
    const newErrors = {};
    
    // Validate individual fields
    newErrors.name = validateField('name', formData.name);
    newErrors.message = validateField('message', formData.message);
    newErrors.phone = validateField('phone', formData.phone);
    newErrors.email = validateField('email', formData.email);
    
    // Validate contact methods
    if (!validateContactMethods()) {
      newErrors.contact = 'Please provide either a phone number or email address';
    }
    
    // Set all errors at once
    setErrors(newErrors);
    
    // Check if any errors exist
    const hasErrors = Object.values(newErrors).some(error => error !== '');
    if (hasErrors) {
      return;
    }
    
    setIsSubmitting(true);

    const trimmedName = formData.name.trim();
    const formattedPhone = formData.phone ? formatPhone(formData.phone) : '';
    
    // Determine primary contact (prefer phone, fallback to email)
    const primaryContact = formattedPhone || formData.email;
    
    const leadData = {
      source: 'Contact Form',
      name: trimmedName,
      contact: primaryContact,
      phone: formattedPhone || null,
      email: formData.email || null,
      note: formData.message,
      meetDateTime: null,
      dogs: null,
    };
    
    // Data for Formspree (keeping original structure)
    const formspreeData = {
      name: trimmedName,
      contact: `Phone: ${formattedPhone || 'Not provided'}, Email: ${formData.email || 'Not provided'}`,
      message: formData.message
    };

    try {
      // Formspree is the email backup; the backend POST is what records the
      // lead in the admin dashboard and fires the push notification. The
      // backend MUST go through csrfFetch — in production csurf guards
      // /api/public/leads, so a bare fetch (no XSRF token) returns 403 and the
      // lead is silently lost while the user still sees a success toast.
      const formspreePromise = fetch("https://formspree.io/f/mlddarlw", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formspreeData),
      })
        .then((res) => res.ok)
        .catch(() => false);

      const backendPromise = csrfFetch("/api/public/leads", {
        method: "POST",
        body: JSON.stringify(leadData),
      })
        .then(() => true)
        .catch((err) => {
          console.error("Backend lead submission failed:", err);
          return false;
        });

      const [formspreeOk, backendOk] = await Promise.all([
        formspreePromise,
        backendPromise,
      ]);

      if (formspreeOk || backendOk) {
        onSuccess();
      } else {
        setErrors((prev) => ({
          ...prev,
          contact: 'Submission failed. Please try again.',
        }));
      }
    } catch (error) {
      console.error("Error during submission:", error);
      setErrors(prev => ({ ...prev, contact: 'Submission failed. Please try again.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      headerContent={<h2 className="contact-form-modal__title">Get in touch</h2>}
      ariaLabel="Contact The Dog Lodge"
      customStyles={{ container: { maxWidth: '540px' } }}
    >
      <p className="contact-form-modal__message">
        Whether you're curious about our services or ready to set up a meet and greet, we're here to help!
      </p>
      <form noValidate onSubmit={handleSubmit} className="contact-form-modal__form">
        <div className="contact-form-modal__contact-section">
          <p className="contact-form-modal__contact-note" id="contact-method-hint">
            Please provide at least one way to reach you
          </p>
          <div className="contact-form-modal__contact-grid">
            <FormField
              id="contact-form-phone"
              name="phone"
              label="Phone Number"
              type="tel"
              value={formData.phone}
              onChange={handleInput}
              onBlur={handleBlur}
              placeholder="(555) 123-4567"
              autoComplete="tel"
              inputMode="tel"
              error={errors.phone}
            />
            <FormField
              id="contact-form-email"
              name="email"
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleInput}
              onBlur={handleBlur}
              placeholder="your@email.com"
              autoComplete="email"
              error={errors.email}
            />
          </div>
          <div
            className="contact-form-modal__contact-error"
            role={errors.contact ? 'alert' : undefined}
            aria-live="polite"
          >
            {errors.contact || ''}
          </div>
        </div>
        <FormField
          id="contact-form-name"
          name="name"
          label="Name"
          type="text"
          value={formData.name}
          onChange={handleInput}
          onBlur={handleBlur}
          autoComplete="name"
          required
          error={errors.name}
        />
        <FormField
          id="contact-form-message"
          name="message"
          label="Message"
          as="textarea"
          rows={4}
          value={formData.message}
          onChange={handleInput}
          onBlur={handleBlur}
          required
          error={errors.message}
        />
        <div className="contact-form-modal__actions">
          <button
            type="button"
            onClick={onClose}
            className="contact-form-modal__btn contact-form-modal__btn--cancel"
          >
            Cancel
          </button>
          <button
            disabled={!isFormValid() || isSubmitting}
            type="submit"
            className="contact-form-modal__btn contact-form-modal__btn--submit"
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
        </div>
        <p className="contact-form-modal__disclaimer">
          We respect your privacy. You will not be signed up for any newsletters or spam.
        </p>
      </form>
    </ModalWrapper>
  );
};

export default ContactFormModal;
